import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { formatUnits, parseUnits } from 'viem';
import { useAccount, usePublicClient } from 'wagmi';
import { usePoolInfo, usePoolUserInfo, usePendingReward, useTokenBalance, useTokenAllowanceStaking, usePoolAPR } from '../hooks/useStaking';
import { useStakingActions } from '../hooks/useStakingActions';
import StakingModal from './StakingModal';
import { STAKING_CONTRACT, MASTERCHEF_ABI } from '../config/staking';
import { getTokenLogo, generatePlaceholderLogo } from '../utils/tokenLogo';
import '../App.css';

const StakingPoolCard = ({ pid, onConnectWallet }) => {
  const { address, isConnected } = useAccount();
  const publicClient = usePublicClient();

  const { data: poolInfo, isLoading: loadingPool } = usePoolInfo(pid);
  const { data: userInfo } = usePoolUserInfo(pid, isConnected ? address : null);
  const { data: pendingReward } = usePendingReward(pid, isConnected ? address : null);
  const { data: tokenBalance } = useTokenBalance(poolInfo?.lpToken, isConnected ? address : null);
  const { data: allowance, refetch: refetchAllowance } = useTokenAllowanceStaking(poolInfo?.lpToken, isConnected ? address : null);
  const { data: apr } = usePoolAPR(pid);

  // Use staking actions hook
  const {
    isApproving,
    isProcessing,
    handleApprove,
    handleStake: hookHandleStake,
    handleUnstake: hookHandleUnstake,
    handleHarvest: hookHandleHarvest,
  } = useStakingActions({
    pid,
    poolInfo,
    address,
    tokenBalance,
    allowance,
    refetchAllowance,
  });

  const [showStakeModal, setShowStakeModal] = useState(false);
  const [modalMode, setModalMode] = useState('stake'); // 'stake' or 'unstake'
  const [tokenLogo, setTokenLogo] = useState(null);
  
  // Harvest cooldown states
  const [harvestCooldown, setHarvestCooldown] = useState(0); // seconds remaining
  const [canHarvestNow, setCanHarvestNow] = useState(true);
  
  // Pending reward counter animation state
  const [displayPendingReward, setDisplayPendingReward] = useState(0);

  // Counter animation helper for pending rewards
  const animatePendingReward = (targetValue) => {
    const duration = 800; // 0.8 seconds
    const steps = 30;
    const stepDuration = duration / steps;
    
    let currentStep = 0;
    const startValue = displayPendingReward;
    const difference = targetValue - startValue;
    
    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      const easeProgress = 1 - Math.pow(1 - progress, 2); // Ease-out quad
      
      setDisplayPendingReward(startValue + (difference * easeProgress));
      
      if (currentStep >= steps) {
        clearInterval(interval);
        setDisplayPendingReward(targetValue);
      }
    }, stepDuration);
    
    return interval;
  };

  // Animate pending rewards with 15-second refresh
  useEffect(() => {
    if (!isConnected || !pendingReward) {
      setDisplayPendingReward(0);
      return;
    }
    
    const targetValue = parseFloat(formatUnits(pendingReward, 18));
    const interval = animatePendingReward(targetValue);
    
    return () => clearInterval(interval);
  }, [pendingReward, isConnected]);

  // Harvest cooldown effect - OPTIMIZED
  useEffect(() => {
    if (!userInfo || !isConnected) {
      setHarvestCooldown(0);
      setCanHarvestNow(true);
      return;
    }

    const checkCooldown = async () => {
      if (!publicClient) return;
      
      try {
        // Get userPosition to check lastHarvest
        const userPosition = await publicClient.readContract({
          address: STAKING_CONTRACT,
          abi: MASTERCHEF_ABI,
          functionName: 'userPosition',
          args: [BigInt(pid), address],
        });
        
        const lastHarvest = Number(userPosition[4]); // lastHarvest timestamp
        const HARVEST_INTERVAL = 3600; // 1 hour in seconds
        
        if (lastHarvest === 0) {
          setCanHarvestNow(true);
          setHarvestCooldown(0);
          return;
        }
        
        const now = Math.floor(Date.now() / 1000);
        const timeSinceHarvest = now - lastHarvest;
        const remaining = Math.max(0, HARVEST_INTERVAL - timeSinceHarvest);
        
        setHarvestCooldown(remaining);
        setCanHarvestNow(remaining === 0);
      } catch (error) {
        console.error(`[Pool ${pid}] Error checking harvest cooldown:`, error);
        setCanHarvestNow(true);
      }
    };

    checkCooldown();
    // Reduced frequency: check every 30 seconds instead of 5
    const interval = setInterval(checkCooldown, 30000);

    return () => clearInterval(interval);
  }, [userInfo, isConnected, publicClient, address, pid]);

  // Countdown timer for harvest cooldown - OPTIMIZED
  useEffect(() => {
    if (harvestCooldown > 0) {
      const timer = setInterval(() => {
        setHarvestCooldown(prev => {
          const newValue = prev - 1;
          if (newValue <= 0) {
            setCanHarvestNow(true);
            return 0;
          }
          return newValue;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [harvestCooldown > 0]); // Only depend on boolean, not the actual value

  // Load token logo
  useEffect(() => {
    if (poolInfo?.lpToken && poolInfo?.tokenInfo?.symbol) {
      getTokenLogo(poolInfo.lpToken).then(logo => {
        setTokenLogo(logo || generatePlaceholderLogo(poolInfo.tokenInfo.symbol));
      });
    }
  }, [poolInfo]);

  // No body scroll lock needed - modal is position fixed and will stay centered

  if (loadingPool) {
    return (
      <div className="staking-pool-card">
        <div className="staking-pool-loading">Loading pool...</div>
      </div>
    );
  }

  if (!poolInfo || !poolInfo.tokenInfo) {
    return null;
  }

  const { tokenInfo, totalStaked } = poolInfo;
  const userStaked = userInfo?.amount || 0n;
  const hasStaked = userStaked > 0n;
  
  // Check if user needs approval (allowance is 0 or too low)
  // For first-time stakers, we need approval before they can stake
  const needsApprovalForStaking = !allowance || allowance === 0n;

  const openModal = (mode) => {
    setModalMode(mode);
    setShowStakeModal(true);
  };

  const closeModal = () => {
    setShowStakeModal(false);
    setModalMode(null);
  };

  // Wrapper functions to match hook signatures
  const handleStake = (amount) => hookHandleStake(amount, tokenInfo.decimals);
  const handleUnstake = (amount) => hookHandleUnstake(amount, tokenInfo.decimals);
  const handleHarvest = () => hookHandleHarvest();
  
  // Approve wrapper - no need to pass any args, approval is for max amount
  const handleApproveWrapper = async () => {
    await handleApprove();
    // After successful approval, refetch allowance to update UI
    await refetchAllowance();
  };

  return (
    <>
      <motion.div 
        className="staking-pool-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.3 + (pid * 0.05) }}
      >
        {/* Pool Header */}
        <div className="staking-pool-header">
          <div className="staking-pool-token">
            {tokenLogo ? (
              <img src={tokenLogo} alt={tokenInfo.symbol} className="staking-token-logo" />
            ) : (
              <div className="staking-token-logo-placeholder">{tokenInfo.symbol?.[0] || '?'}</div>
            )}
            <div>
              <h3 className="staking-pool-name">{pid === 1 ? 'LRST BNB-LP' : tokenInfo.symbol} Pool</h3>
              <p className="staking-pool-pid">Earn LRST</p>
            </div>
          </div>
          <div className="staking-pool-apr">
            <span className="staking-apr-label">APR</span>
            <span className="staking-apr-value">
              {apr ? Math.floor(apr) : '0'}%
            </span>
          </div>
        </div>

        {/* Pool Stats - Always Visible */}
        <div className="staking-pool-stats">
          <div className="staking-stat staking-stat-boxed">
            <span className="staking-stat-label">Total Staked</span>
            <span className="staking-stat-value staking-highlight">
              {poolInfo 
                ? parseFloat(formatUnits(poolInfo.totalStaked, poolInfo.tokenInfo?.decimals || 18))
                    .toLocaleString(undefined, { 
                      minimumFractionDigits: pid === 1 ? 6 : 0,
                      maximumFractionDigits: pid === 1 ? 6 : 0 
                    })
                : '0'
              } {pid === 1 ? 'LRST BNB-LP' : tokenInfo.symbol}
            </span>
          </div>
          
          <div className="staking-stat staking-stat-boxed">
            <span className="staking-stat-label">Your Deposit</span>
            <span className="staking-stat-value staking-highlight">
              {isConnected && userInfo
                ? parseFloat(formatUnits(userInfo.amount || 0n, poolInfo?.tokenInfo?.decimals || 18))
                    .toLocaleString(undefined, { 
                      minimumFractionDigits: pid === 1 ? 6 : 0,
                      maximumFractionDigits: pid === 1 ? 6 : 2
                    })
                : '0'
              } {pid === 1 ? 'LRST BNB-LP' : tokenInfo.symbol}
            </span>
          </div>
          
          <div className="staking-stat staking-stat-pending">
            <div className="staking-stat-pending-content">
              <span className="staking-stat-label">Pending Rewards</span>
              <span className="staking-stat-value staking-reward-value">
                {isConnected && pendingReward
                  ? displayPendingReward.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })
                  : '0.00'
                } LRST
              </span>
            </div>
            {isConnected && pendingReward > 0n && (
              <motion.button
                className="staking-harvest-btn"
                onClick={handleHarvest}
                disabled={isProcessing || !canHarvestNow}
                data-testid="harvest-btn"
                style={{
                  cursor: isProcessing || !canHarvestNow ? 'not-allowed' : 'pointer',
                  opacity: isProcessing || !canHarvestNow ? 0.6 : 1
                }}
                whileHover={{ scale: isProcessing || !canHarvestNow ? 1 : 1.05 }}
                whileTap={{ scale: isProcessing || !canHarvestNow ? 1 : 0.95 }}
              >
                {isProcessing ? '...' : 
                 !canHarvestNow && harvestCooldown > 0 ? 
                  `${Math.floor(harvestCooldown / 60)}m ${harvestCooldown % 60}s` : 
                 'Harvest'}
              </motion.button>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="staking-pool-actions">
          {!isConnected ? (
            <div className="staking-btn staking-btn-disabled-static">
              CONNECT WALLET
            </div>
          ) : !hasStaked ? (
            // First-time user: Show Approve button if not approved yet
            needsApprovalForStaking ? (
              <motion.button
                className="staking-btn staking-btn-warning"
                onClick={handleApproveWrapper}
                disabled={isApproving}
                data-testid="approve-btn"
                whileHover={{ scale: isApproving ? 1 : 1.02 }}
                whileTap={{ scale: isApproving ? 1 : 0.98 }}
              >
                {isApproving ? 'Approving...' : 'Approve'}
              </motion.button>
            ) : (
              <motion.button
                className="staking-btn staking-btn-primary"
                onClick={() => openModal('stake')}
                data-testid="stake-btn"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Stake
              </motion.button>
            )
          ) : (
            <div className="staking-btn-group">
              <motion.button
                className="staking-btn staking-btn-secondary"
                onClick={() => openModal('unstake')}
                data-testid="unstake-btn"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Unstake
              </motion.button>
              <motion.button
                className="staking-btn staking-btn-primary"
                onClick={() => openModal('addmore')}
                data-testid="add-more-btn"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Add More
              </motion.button>
            </div>
          )}
        </div>
      </motion.div>

      {/* Stake/Unstake Modal */}
      <StakingModal
        isOpen={showStakeModal}
        onClose={closeModal}
        mode={modalMode}
        tokenSymbol={pid === 1 ? 'LRST BNB-LP' : tokenInfo.symbol}
        tokenDecimals={tokenInfo.decimals}
        balance={modalMode === 'unstake' ? userStaked : tokenBalance}
        allowance={allowance}
        isApproving={isApproving}
        isProcessing={isProcessing}
        onApprove={handleApprove}
        onStake={handleStake}
        onUnstake={handleUnstake}
      />
    </>
  );
};

export default StakingPoolCard;
